/**
 * The member context for matching (TRUS-757, contract item 10): the org's
 * configured member fields, read-only, plus the health plan. Values come from
 * the server, already scoped to the active org.
 */

import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { getApiErrorMessage } from '@/utils/apiErrors';
import { closedLoopReferralsAPI, type MemberContext } from '@/services/api/closedLoopReferrals';

interface MemberContextPanelProps {
  memberId: number;
  /** Receives the context once loaded (e.g. to prefill a zip filter). */
  onLoaded?: (ctx: MemberContext) => void;
}

export function displayValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '';
  if (Array.isArray(v)) return v.map(displayValue).filter(Boolean).join(', ');
  if (typeof v === 'boolean') return v ? '✓' : '✗';
  if (typeof v === 'object') return Object.values(v as Record<string, unknown>).map(displayValue).filter(Boolean).join(', ');
  return String(v);
}

/** The member's zip from the configured fields, if one is there. */
export function contextZip(ctx: MemberContext | null): string {
  if (!ctx) return '';
  const f = ctx.fields.find((x) => /^(zip|zip_code|zipcode|postal_code)$/i.test(x.name));
  return f ? displayValue(f.value).slice(0, 10) : '';
}

export function MemberContextPanel({ memberId, onLoaded }: MemberContextPanelProps) {
  const { t } = useAppTranslation();
  const [ctx, setCtx] = useState<MemberContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    closedLoopReferralsAPI
      .getMemberContext(memberId)
      .then((c) => {
        if (cancelled) return;
        setCtx(c);
        onLoaded?.(c);
      })
      .catch((err) => {
        if (!cancelled) setError(getApiErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  return (
    <section className="rounded-lg border border-v2border bg-white p-4 dark:bg-surface" data-testid="member-context-panel">
      <h3 className="mb-3 text-sm font-semibold text-ink">{t('referralHub.context.title', 'Member')}</h3>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-ink-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          {t('referralHub.context.loading', 'Loading member details…')}
        </div>
      ) : error ? (
        <p className="text-sm text-danger-ink" role="alert">
          {error}
        </p>
      ) : ctx ? (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          {ctx.fields
            .filter((f) => displayValue(f.value))
            .map((f) => (
              <div key={f.name} className="min-w-0">
                <dt className="text-[11px] uppercase tracking-[0.04em] text-ink-400">{f.label}</dt>
                <dd className="truncate text-ink">{displayValue(f.value)}</dd>
              </div>
            ))}
          <div className="min-w-0">
            <dt className="text-[11px] uppercase tracking-[0.04em] text-ink-400">
              {t('referralHub.context.healthPlan', 'Health plan')}
            </dt>
            <dd className="text-ink">
              {ctx.health_plan?.payer_name || t('referralHub.context.noPlan', 'None on file')}
              {ctx.health_plan?.member_plan_id && (
                <span className="ml-1 text-ink-500">
                  {t('referralHub.context.planId', '(ID {{id}})', { id: ctx.health_plan.member_plan_id })}
                </span>
              )}
            </dd>
          </div>
          {ctx.medi_cal_id && (
            <div className="min-w-0">
              <dt className="text-[11px] uppercase tracking-[0.04em] text-ink-400">
                {t('referralHub.context.mediCal', 'Medi-Cal ID')}
              </dt>
              <dd className="text-ink">{ctx.medi_cal_id}</dd>
            </div>
          )}
          {ctx.program_enrollments.length > 0 && (
            <div className="min-w-0 sm:col-span-2">
              <dt className="text-[11px] uppercase tracking-[0.04em] text-ink-400">
                {t('referralHub.context.programs', 'Programs')}
              </dt>
              <dd className="text-ink">{ctx.program_enrollments.map((p) => p.program_name).join(', ')}</dd>
            </div>
          )}
        </dl>
      ) : null}
    </section>
  );
}

export default MemberContextPanel;
